/**
 * Página do Dashboard - Versão Minimalista com Drag & Drop
 * Visão geral com métricas e resumos personalizáveis
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import './Dashboard.css';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getMetricasClinica } from '../../services/clinicaDashboardService';
import clinicaService from '../../services/clinicaService';

import Navbar from '../../components/Navbar';
import Sidebar from '../../components/Sidebar';
import SettingsModalClinica from '../../components/SettingsModalClinica';
import DashboardSidebar from '../../components/DashboardSidebar';
import BirthdayMessageModal from '../../components/BirthdayMessageModal';


/**
 * Página do Dashboard
 */

const ClinicaDashboard = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metricas, setMetricas] = useState(null);


  // Controle do menu lateral
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Controle do modal de configurações
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Configuração de layout (1, 2 ou 3 colunas)
  const [numColunas, setNumColunas] = useState(3);
  // No celular ignora a preferência de colunas e força 1 — evita cards e gráficos espremidos
  const isMobile = useMediaQuery('(max-width:768px)');
  const colunasEfetivas = isMobile ? 1 : numColunas;

  // Visibilidade dos cards
  const [cardsVisiveis, setCardsVisiveis] = useState({
    resultado: true,
    impostos: true,
    fluxo: true,
    despesas: true,
    dre: true,
    lancamentos: true,
  });

  // Estado de expansão dos cards (acordeão)
  const [cardsExpandidos, setCardsExpandidos] = useState({
    dre: true,
    resultado: true,
    impostos: true,
    fluxo: true,
    despesas: true,
    lancamentos: true,
  });

  // Ordem dos cards (para drag and drop)
  const [ordemCards, setOrdemCards] = useState([
    'resultado',
    'impostos',
    'fluxo',
    'despesas',
    'dre',
    'lancamentos',
  ]);

  // Estado para drag and drop (API HTML5 nativa)
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Data atual (agora controlável)
  const dataAtual = new Date();
  const [mesAtual, setMesAtual] = useState(dataAtual.getMonth() + 1);
  const [anoAtual, setAnoAtual] = useState(dataAtual.getFullYear());
  const [mostrarTodosAniversariantes, setMostrarTodosAniversariantes] = useState(false);
  const [mostrarTodosRetencao, setMostrarTodosRetencao] = useState(false);

  // Clínica logada (nome real + mensagem de aniversário personalizada)
  const [clinicaInfo, setClinicaInfo] = useState({ nome: '', mensagemAniversario: '' });
  const [pacienteAniversarioSelecionado, setPacienteAniversarioSelecionado] = useState(null);

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  /**
   * Handler para mudança de mês no sidebar
   */
  const handleMesChange = (novoMes, novoAno) => {
    setMesAtual(novoMes);
    setAnoAtual(novoAno);
  };

  /**
   * Alterna expansão/recolhimento do card
   */
  const toggleCardExpansao = (cardId) => {
    const novosCardsExpandidos = {
      ...cardsExpandidos,
      [cardId]: !cardsExpandidos[cardId]
    };
    setCardsExpandidos(novosCardsExpandidos);
    localStorage.setItem('clinicaDashboardCardsExpandidos', JSON.stringify(novosCardsExpandidos));
  };

  /**
   * Handlers para drag and drop HTML5 nativo (funciona perfeitamente em React)
   */
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };


  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newOrder = [...ordemCards];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);
    setOrdemCards(newOrder);
    setDraggedIndex(index);
  // Salva ordem no localStorage a cada alteração
  localStorage.setItem('clinicaDashboardOrdemCards', JSON.stringify(newOrder));
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    // Ordem já salva no handleDragOver
  };

  /**
   * Busca métricas do backend (apenas odontologia)
   */
  const carregarMetricas = async () => {
    try {
      setLoading(true);
      setError('');
      const dados = await getMetricasClinica(mesAtual, anoAtual);
      console.log('Dados recebidos do backend:', dados); // Log para debug
      window.metricasDebug = dados;
      if (dados && dados.success) {
        setMetricas(dados);
      } else {
        setMetricas(null);
        setError(dados && dados.message ? dados.message : 'Dados inválidos do backend');
      }
    } catch (err) {
      setError('Erro ao carregar dados do dashboard');
      setMetricas(null);
    } finally {
      setLoading(false);
    }
  };

  // Carregar preferências salvas ao montar o componente
  useEffect(() => {
  const savedNumColunas = localStorage.getItem('clinicaDashboardNumColunas');
  const savedCardsVisiveis = localStorage.getItem('clinicaDashboardCardsVisiveis');
  const savedCardsExpandidos = localStorage.getItem('clinicaDashboardCardsExpandidos');
  const savedOrdemCards = localStorage.getItem('clinicaDashboardOrdemCards');

    if (savedNumColunas) {
      setNumColunas(parseInt(savedNumColunas));
    }
    if (savedCardsVisiveis) {
      setCardsVisiveis(JSON.parse(savedCardsVisiveis));
    }
    if (savedCardsExpandidos) {
      setCardsExpandidos(JSON.parse(savedCardsExpandidos));
    }
    if (savedOrdemCards) {
      try {
        const parsed = JSON.parse(savedOrdemCards);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setOrdemCards(parsed);
        }
      } catch (e) {}
    }
  }, []);


  // Carregar métricas ao mudar mês/ano
  useEffect(() => {
    carregarMetricas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesAtual, anoAtual]);

  // Carregar nome real e mensagem de aniversário personalizada da clínica logada
  useEffect(() => {
    if (!user?.clinicaId) return;
    clinicaService.buscarClinica(user.clinicaId)
      .then((dados) => {
        setClinicaInfo({
          nome: dados?.nome || '',
          mensagemAniversario: dados?.mensagemAniversario || '',
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.clinicaId]);

  const handleSalvarMensagemAniversario = async (novaMensagem) => {
    await clinicaService.atualizarClinica(user.clinicaId, { mensagemAniversario: novaMensagem });
    setClinicaInfo((prev) => ({ ...prev, mensagemAniversario: novaMensagem }));
  };

  const handleSaveSettings = (novoNumColunas, novosCardsVisiveis) => {
  setNumColunas(novoNumColunas);
  setCardsVisiveis(novosCardsVisiveis);
  localStorage.setItem('clinicaDashboardNumColunas', novoNumColunas);
  localStorage.setItem('clinicaDashboardCardsVisiveis', JSON.stringify(novosCardsVisiveis));
  };

  const cardAccentColors = {
    resultado:   '#22C55E',  // verde — base de pacientes (positivo)
    impostos:    '#F97316',  // laranja — conversão
    fluxo:       '#F59E0B',  // âmbar — aniversariantes
    despesas:    '#EA580C',  // laranja escuro — top procedimentos
    lancamentos: '#EF4444',  // vermelho — retenção/alertas
    dre:         '#FB923C',  // laranja claro — fluxo de pacientes
  };

  // Definição dos cards com configuração
  const cardsConfig = {
    resultado: {
      titulo: 'Base Geral de Pacientes',
      visivel: cardsVisiveis.resultado,
      expandido: cardsExpandidos.resultado,
      renderConteudo: () => {
        if (!metricas) {
          return <pre style={{ color: 'red', fontSize: '0.9em' }}>Sem dados do backend<br />{JSON.stringify(metricas, null, 2)}</pre>;
        }
        const total = metricas.totalPacientes ?? 0;
        const ativos = metricas.pacientesAtivos ?? 0;
        const novosMes = metricas.novosPacientesMes ?? 0;
        const inativos = total - ativos;
        // Crescimento: % de novos cadastros sobre total
        const crescimentoMes = total > 0 ? ((novosMes / total) * 100).toFixed(1) : 0;
        // Média semanal: novos cadastros / número de semanas do mês
        const semanasNoMes = 4.3; // Aproximação
        const mediaSemanal = Math.round(novosMes / semanasNoMes);
        // Progresso: % de ativos sobre total
        const progresso = total > 0 ? ((ativos / total) * 100).toFixed(0) : 0;
        return (
          <div className="card">
            <div className="big-number">{total}</div>
            <div className="label">Pacientes totais no sistema</div>
            <div style={{ marginTop: 20 }}>
              <div className="kpi-row">
                <span className="label">Pacientes Ativos</span>
                <span className="value" style={{ color: "var(--secondary)" }}>{ativos}</span>
              </div>
              <div className="kpi-row">
                <span className="label">Inativos (+6 meses)</span>
                <span className="value">{inativos}</span>
              </div>
              <div className="progress-container" style={{ marginTop: 15 }}>
                <div className="label" style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span>Crescimento da Base</span>
                  <span style={isDarkMode ? { color: '#fff' } : { color: "var(--secondary)" }}>+{crescimentoMes}% este mês</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progresso}%`, background: "#16a34a" }}></div>
                </div>
              </div>
            </div>
            <p className="label" style={{ marginTop: 15, fontSize: "0.8rem" }}>
              Média de <strong>{mediaSemanal} novos cadastros</strong>/semana
            </p>
          </div>
        );
      }
    },
    impostos: {
      titulo: 'Conversão de Orçamentos',
      visivel: cardsVisiveis.impostos,
      expandido: cardsExpandidos.impostos,
      renderConteudo: () => {
        if (!metricas) {
          return <div className="card"><span style={{ color: 'red' }}>Sem dados do backend</span></div>;
        }
        // Dados reais do backend
        const avaliacoes = metricas.totalAvaliacoes ?? 0;
        const tratamentosFechados = metricas.totalTratamentosFechados ?? 0;
        const metaConversao = metricas.metaConversao ?? 75;
        // Conversão: % de tratamentos fechados sobre avaliações
        const conversao = avaliacoes > 0 ? ((tratamentosFechados / avaliacoes) * 100).toFixed(0) : 0;
        return (
          <div className="card">
            <div className="big-number">{conversao}%</div>
            <div className="kpi-row">
              <span className="label">Avaliações Realizadas</span>
              <span className="value">{avaliacoes}</span>
            </div>
            <div className="kpi-row">
              <span className="label">Tratamentos Fechados</span>
              <span className="value">{tratamentosFechados}</span>
            </div>
            <div className="progress-container">
              <div className="label" style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span>Meta de Conversão</span>
                <span>{metaConversao}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${conversao}%`, background: "#16a34a" }}></div>
              </div>
            </div>
          </div>
        );
      }
    },
    fluxo: {
      titulo: 'Aniversariantes do Mês',
      visivel: cardsVisiveis.fluxo,
      expandido: cardsExpandidos.fluxo,
      renderConteudo: () => {
        //
        if (!metricas || !Array.isArray(metricas.aniversariantesMes)) {
          return (
            <div className="card">
              <div style={{ color: 'red', fontSize: '0.95em' }}>Sem aniversariantes cadastrados neste mês.</div>
            </div>
          );
        }
        const hoje = new Date();
        // Corrige bug de fuso horário ao exibir datas YYYY-MM-DD
        const parseDataLocal = (dataStr) => {
          if (!dataStr) return null;
          const [ano, mes, dia] = dataStr.split('-');
          return new Date(Number(ano), Number(mes) - 1, Number(dia));
        };
        const formatarData = (dataStr) => {
          const data = parseDataLocal(dataStr);
          if (!data) return '';
          const dia = data.getDate().toString().padStart(2, '0');
          const mesNome = meses[data.getMonth()];
          return `${dia} de ${mesNome}`;
        };
        const getBdayLabel = (dataStr) => {
          const data = parseDataLocal(dataStr);
          if (!data) return '';
          const hojeStr = `${hoje.getFullYear()}-${(hoje.getMonth()+1).toString().padStart(2,'0')}-${hoje.getDate().toString().padStart(2,'0')}`;
          const bdayStr = `${data.getFullYear()}-${(data.getMonth()+1).toString().padStart(2,'0')}-${data.getDate().toString().padStart(2,'0')}`;
          if (bdayStr === hojeStr) return `Hoje - ${formatarData(dataStr)}`;
          // Amanhã
          const amanha = new Date(hoje);
          amanha.setDate(hoje.getDate() + 1);
          const amanhaStr = `${amanha.getFullYear()}-${(amanha.getMonth()+1).toString().padStart(2,'0')}-${amanha.getDate().toString().padStart(2,'0')}`;
          if (bdayStr === amanhaStr) return `Amanhã - ${formatarData(dataStr)}`;
          return formatarData(dataStr);
        };
        if (metricas.aniversariantesMes.length === 0) {
          return (
            <div className="card">
              <div style={{ color: 'gray', fontSize: '0.95em' }}>Nenhum aniversariante neste mês.</div>
            </div>
          );
        }
        const listaExibida = mostrarTodosAniversariantes
          ? metricas.aniversariantesMes
          : metricas.aniversariantesMes.slice(0, 5);
        return (
          <div className="card">
            <div className="bday-list">
              {listaExibida.map((p, idx) => (
                <div className="bday-item" key={p.id || idx}>
                  <div className="bday-info">
                    <span
                      className="bday-name"
                      style={isDarkMode ? { color: '#fff' } : {}}
                    >
                      <span style={isDarkMode ? { color: '#fff' } : { color: '#333' }}>
                        {p.nome.length > 28 ? p.nome.substring(0, 28) + '…' : p.nome}
                      </span> {getBdayLabel(p.dataNascimento) === `Hoje - ${formatarData(p.dataNascimento)}` && <span className="gift-icon">🎁</span>}
                    </span>
                    <span
                      className="bday-date"
                      style={isDarkMode ? { color: '#fff' } : {}}
                    >
                      {getBdayLabel(p.dataNascimento)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="mkt-whatsapp-btn"
                    onClick={() => setPacienteAniversarioSelecionado(p)}
                  >
                    MANDAR PRESENTE
                  </button>
                </div>
              ))}
            </div>
            {metricas.aniversariantesMes.length > 5 && (
              <button
                className="view-more-btn"
                onClick={() => setMostrarTodosAniversariantes(v => !v)}
              >
                {mostrarTodosAniversariantes
                  ? 'Ver menos'
                  : `Ver todos os ${metricas.aniversariantesMes.length} aniversariantes`}
              </button>
            )}
          </div>
        );
      }
    },
    despesas: {
      titulo: 'Top Procedimentos (Valor)',
      visivel: cardsVisiveis.despesas,
      expandido: cardsExpandidos.despesas,
      renderConteudo: () => {
        if (!metricas || !Array.isArray(metricas.topProcedimentos)) {
          return (
            <div className="card">
              <div style={{ color: 'red', fontSize: '0.95em' }}>Sem dados de procedimentos para este mês.</div>
            </div>
          );
        }
        return (
          <div className="card">
            {metricas.topProcedimentos.length === 0 && (
              <div style={{ color: 'gray', fontSize: '0.95em' }}>Nenhum procedimento faturado neste mês.</div>
            )}
            {metricas.topProcedimentos.map((proc, idx) => (
              <div className="kpi-row" key={proc.nome}>
                <span className="label">{idx + 1}. {proc.nome}</span>
                <span className="value">R$ {proc.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        );
      }
    },
     lancamentos: {
      titulo: 'Retenção Ativa (Alertas +6 meses)',
      visivel: cardsVisiveis.lancamentos,
      expandido: cardsExpandidos.lancamentos,
      renderConteudo: () => {
        if (!metricas || !Array.isArray(metricas.pacientesRetencao)) {
          return (
            <div className="card">
              <div style={{ color: 'red', fontSize: '0.95em' }}>Sem pacientes inativos por falta de agendamento.</div>
            </div>
          );
        }
        const listaRetencao = mostrarTodosRetencao
          ? metricas.pacientesRetencao
          : metricas.pacientesRetencao.slice(0, 5);
        return (
          <div className="card">
            <div className="patient-list">
              {metricas.pacientesRetencao.length === 0 && (
                <div style={{ color: 'gray', fontSize: '0.95em' }}>Nenhum paciente ficou inativo nos últimos 6 meses.</div>
              )}
              {listaRetencao.map((p, idx) => (
                <div className="patient-item" key={p.id}>
                  <div className="patient-info">
                    <span className="patient-name" style={isDarkMode ? { color: '#fff' } : { color: '#333' }}>
                      {p.nome && p.nome.length > 28 ? p.nome.substring(0, 28) + '…' : p.nome}
                    </span>
                    <span className="last-procedure">
                      Último: {p.dataUltimoAgendamento ? new Date(p.dataUltimoAgendamento).toLocaleDateString('pt-BR') : 'Nunca agendou'}
                    </span>
                  </div>
                  <a
                    href={`https://wa.me/${p.telefone ? '55' + p.telefone.replace(/\D/g, '') : ''}?text=Olá ${p.nome}, faz tempo que não nos vemos para sua revisão! Que tal agendar uma consulta?`}
                    target="_blank"
                    className="whatsapp-btn"
                  >
                    <span>WhatsApp</span>
                  </a>
                </div>
              ))}
            </div>
            {metricas.pacientesRetencao.length > 5 && (
              <button
                className="view-more-btn"
                onClick={() => setMostrarTodosRetencao(v => !v)}
              >
                {mostrarTodosRetencao
                  ? 'Ver menos'
                  : `Ver todos os ${metricas.pacientesRetencao.length} pacientes`}
              </button>
            )}
          </div>
        );
      }
    },
    dre: {
      titulo: 'Fluxo de Pacientes (Mês)',
      visivel: cardsVisiveis.dre,
      expandido: cardsExpandidos.dre,
      renderConteudo: () => {
        if (!metricas || !metricas.fluxoPacientesMes) {
          return (
            <div className="card">
              <div style={{ color: 'red', fontSize: '0.95em' }}>Sem dados de atendimentos para este mês.</div>
            </div>
          );
        }
        const { total, novos, retornos } = metricas.fluxoPacientesMes;
        // Média diária: total / dias do mês
        const hoje = new Date();
        const diasNoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate();
        const mediaDiaria = diasNoMes > 0 ? (total / diasNoMes).toFixed(1) : 0;
        return (
          <div className="card">
            <div className="big-number">{total}</div>
            <div className="label">Total de atendimentos realizados</div>

            <div style={{ marginTop: 15 }}>
              <div className="kpi-row">
                <span className="label">Novos (Primeira vez)</span>
                <span className="value" style={{ color: "var(--secondary)" }}>{novos}</span>
              </div>
              <div className="kpi-row">
                <span className="label">Retornos / Manutenções</span>
                <span className="value">{retornos}</span>
              </div>
            </div>

            <p className="label" style={{ marginTop: 15, fontSize: "0.8rem" }}>
              Média de <strong>{mediaDiaria} pacientes/dia</strong>
            </p>
          </div>
        );
      }
    },

   
  };

  if (loading) {
    return (
      <>
        <Navbar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{ ml: 0, mt: '50px' }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      {/* Navbar e Sidebar */}
      <Navbar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onSettingsOpen={() => setSettingsModalOpen(true)}
      />

      {/* Menu Lateral Flutuante (Inspirado no Meu Dinheiro) */}
      <DashboardSidebar
        mesAtual={mesAtual}
        anoAtual={anoAtual}
        onMesChange={handleMesChange}
        onConfigOpen={() => setSettingsModalOpen(true)}
      />

      {/* Modal de Configurações */}
        <SettingsModalClinica
          open={settingsModalOpen}
          onClose={() => setSettingsModalOpen(false)}
          numColunas={numColunas}
          cardsVisiveis={cardsVisiveis}
          onSave={handleSaveSettings}
        />

        <BirthdayMessageModal
          open={!!pacienteAniversarioSelecionado}
          onClose={() => setPacienteAniversarioSelecionado(null)}
          paciente={pacienteAniversarioSelecionado}
          mensagemPadrao={`Parabéns {{paciente}}! 🎉 A ${clinicaInfo.nome || 'nossa clínica'} deseja um feliz aniversário! Como presente, você tem um desconto especial em qualquer procedimento este mês...`}
          mensagemPersonalizada={clinicaInfo.mensagemAniversario}
          onSalvarMensagem={handleSalvarMensagemAniversario}
        />

      {/* Conteúdo Principal */}
      <Box
        className="clinica-dashboard"
        sx={{
          flexGrow: 1,
          padding: '0px 0px 0px 0px',
          paddingLeft: { xs: '10px', md: '70px' },
          paddingRight: { xs: '10px', md: 0 },
          backgroundColor: isDarkMode ? 'background.default' : '#FFFAF5',
          minHeight: '100vh',
        }}
      >
        {/* Cabeçalho da página */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 300, fontSize: '1.6em', borderLeft: '4px solid #F97316', paddingLeft: '12px' }}>
              Visão da Clínica
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9em' }}>
            Período: <strong>{meses[mesAtual - 1]}/{anoAtual}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {metricas && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[...Array(colunasEfetivas)].map((_, colIdx) => (
              <Box key={colIdx} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                {ordemCards
                  .map((cardId, globalIdx) => {
                    if (globalIdx % colunasEfetivas !== colIdx) return null;
                    const cardConfig = cardsConfig[cardId];
                    if (!cardConfig || !cardConfig.visivel) return null;
                    return (
                      <Card
                        key={cardId}
                        sx={{
                          transition: 'all 0.2s ease',
                          width: '100%',
                          mb: 0,
                          borderTop: `3px solid ${cardAccentColors[cardId] || '#F97316'}`,
                          overflow: 'hidden',
                          '&:hover': { boxShadow: '0 4px 20px rgba(249,115,22,0.18)' },
                        }}
                        draggable
                        onDragStart={() => handleDragStart(globalIdx)}
                        onDragOver={(e) => handleDragOver(e, globalIdx)}
                        onDragEnd={handleDragEnd}
                      >
                        <CardContent sx={{ pb: cardConfig.expandido ? 2 : '16px !important' }}>
                          <Box
                            className="card-header"
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: cardConfig.expandido ? 2 : 0,
                              borderBottom: '1px solid',
                              borderColor: isDarkMode ? 'rgba(249,115,22,0.25)' : 'rgba(249,115,22,0.18)',
                              pb: 1.5,
                              mx: -2, px: 2, pt: 1.5, mt: -2,
                              background: isDarkMode ? 'rgba(249,115,22,0.07)' : 'rgba(249,115,22,0.04)',
                              '&:hover .icon-button': { opacity: 1 },
                              '&:hover .drag-icon': { opacity: 1 },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DragIndicatorIcon 
                                className="drag-icon"
                                sx={{ color: 'text.disabled', opacity: 0, transition: 'opacity 0.2s ease', fontSize: '1.2em', cursor: 'pointer' }} 
                              />
                              <Typography variant="h6" sx={{ fontSize: '1.1em', fontWeight: 600 }}>
                                {cardConfig.titulo}
                              </Typography>
                            </Box>
                            <IconButton 
                              className="icon-button"
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCardExpansao(cardId);
                              }}
                              sx={{ color: 'text.secondary', opacity: 0, transition: 'opacity 0.2s ease' }}
                            >
                              {cardConfig.expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                          <Collapse in={cardConfig.expandido} timeout="auto" unmountOnExit>
                            {cardConfig.renderConteudo()}
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })}
              </Box>
            ))}
          </Box>
        )}

      </Box>
    </>
  );
};

export default ClinicaDashboard;

